/**
 * Rails — the plan's lines as long, smooth polylines.
 *
 * The vectoriser breaks every line at each junction (a leader crossing a duct
 * bank's edge, a tick on a centreline) and at each gap (a dash, a symbol
 * sitting on the line). A rail re-assembles one physical line from those
 * pieces: starting at a seed segment it continues through junctions along the
 * straightest continuation and jumps small gaps in the same direction, in both
 * directions, until the line bends sharply or ends.
 *
 * Rails are fixed geometric objects, cached per seed segment, so the tracer
 * can use them as the sides of a band directly: a mark's border then sits
 * exactly on the plan's line work and never moves once drawn.
 */
import type { Seg, VectorPlan } from './types.ts';
import type { Vec } from '../../shared/doc.ts';
import { nearestOnSegment, simplify } from '../editor/geom.ts';

const TURN_COS = Math.cos((28 * Math.PI) / 180); // max turn at a junction to count as the same line
const THIN_RATIO = 0.66; // a continuation may not be thinner than this fraction of the rail's weight (a lighter line is another line)
const THICK_RATIO = 0.6; // … nor much thicker (a dashed centreline must not swallow a solid edge); merged blobs are always passable
const JUMP_COS = Math.cos((22 * Math.PI) / 180); // max direction change across a gap
const DASH_TURN_COS = Math.cos((8 * Math.PI) / 180); // a dashed line turning more than this at a junction more likely met a line crossing it
const DASH_STRAIGHT_COS = Math.cos((6 * Math.PI) / 180); // … and its next dash, straight on across the gap, is the line
const THROUGH_COS = Math.cos((45 * Math.PI) / 180); // a line carries on past a rail's end when a piece leaves within this turn
const THROUGH_PARTNER_COS = Math.cos((25 * Math.PI) / 180); // two pieces at a node this close to opposite are one line passing through
const JUMP = 56; // widest gap a rail jumps (image px): a symbol on the line, a dash gap, removed text
const PROBE = 12; // length along a segment used to measure its direction at an end (image px)
const LOOK = 40; // a continuation is judged by direction over this much line behind and ahead, not by one piece
const TRUST_LEN = 30; // pieces at least this long have a trustworthy thickness
const BLOB_RATIO = 1.3; // … unless much thicker than the plan's heaviest lines: two lines running merged
const HEAVY_RUN = 60; // … or a line drawn heavy on purpose (a trench, a right of way): this much of it at one weight, dash after dash, says so
const MAX_SEGS = 800;
const MAX_LEN = 12000; // image px
const BLOCK = 8; // polyline segments per bounding box block (projection speed-up)

export interface Rail {
  id: number;
  segs: number[]; // seg ids in rail order
  segLens: number[]; // length of each of those pieces (image px)
  pts: number[]; // polyline, image px (start → end)
  cum: Float64Array; // cumulative arc length per vertex
  len: number;
  thick: number; // length-weighted mean line thickness (image px)
  inkLen: number; // length on ink
  gapLen: number; // length jumped across gaps
  jumps: number;
  dashed: boolean; // dashed / dash-dot line work (short runs, frequent gaps)
  bbox: Float64Array; // per block: minX, minY, maxX, maxY
  throughStart: boolean; // the line physically continues past this end (the rail stopped for weight or turn)
  throughEnd: boolean;
  loop: boolean; // a closed symbol (a circle), never a line to follow
}

export interface Projection {
  s: number; // arc length of the nearest point
  d: number; // distance from the query point
  x: number;
  y: number;
  tx: number; // unit tangent of the rail at the nearest point (rail direction)
  ty: number;
  over: number; // how far the query point lies beyond the rail's end along its end tangent (0 inside)
}

interface Step {
  seg: number;
  fwd: boolean; // traversed a → b
  gap: number; // gap jumped to reach this segment (0 when joined at a node)
}

export class RailIndex {
  readonly plan: VectorPlan;
  #near: (p: Vec, radius: number) => number[];
  #bySeed = new Map<number, Rail>();
  #rails: Rail[] = [];
  readonly heavy: number; // the plan's heavy line weight (length-weighted 90th percentile of long pieces)

  constructor(plan: VectorPlan, segsNear: (p: Vec, radius: number) => number[]) {
    this.plan = plan;
    this.#near = segsNear;
    const long = plan.segs.filter((s) => s.len >= 40 && !s.leader).sort((a, b) => a.thick - b.thick);
    const total = long.reduce((n, s) => n + s.len, 0);
    let acc = 0,
      heavy = 3;
    for (const s of long) {
      acc += s.len;
      heavy = s.thick;
      if (acc >= 0.9 * total) break;
    }
    this.heavy = Math.max(1, heavy);
  }

  get count(): number {
    return this.#rails.length;
  }

  /** The rail through a segment (grown from it once, then cached). */
  railOf(segId: number): Rail {
    const hit = this.#bySeed.get(segId);
    if (hit) return hit;
    const rail = this.#build(segId);
    this.#bySeed.set(segId, rail);
    this.#rails.push(rail);
    return rail;
  }

  /* ------------------------------------------------------------ growth */

  #build(seed: number): Rail {
    const segs = this.plan.segs;
    const visited = new Set<number>([seed]);
    // (what the line's weight turned out to be ahead is its weight behind the seed too)
    const weight = { sum: 0, len: 0, run: 0, runSum: 0, gaps: 0, seeded: false };
    const ahead = this.#grow(seed, true, visited, weight);
    const behind = this.#grow(seed, false, visited, weight);
    const throughEnd = ahead.through,
      throughStart = behind.through;
    // rail order: behind (reversed, each piece flipped), the seed, ahead
    const order: Step[] = [];
    for (let i = behind.length - 1; i >= 0; i--) {
      const st = behind[i]!;
      order.push({ seg: st.seg, fwd: !st.fwd, gap: i + 1 < behind.length ? behind[i + 1]!.gap : 0 });
    }
    order.push({ seg: seed, fwd: true, gap: behind.length ? behind[0]!.gap : 0 });
    for (const st of ahead) order.push(st);
    const pts: number[] = [];
    let inkLen = 0,
      gapLen = 0,
      jumps = 0,
      thickSum = 0;
    const ids: number[] = [],
      lens: number[] = [];
    for (const st of order) {
      const s = segs[st.seg]!;
      ids.push(s.id);
      lens.push(s.len);
      inkLen += s.len;
      thickSum += s.thick * s.len;
      if (st.gap > 0) {
        gapLen += st.gap;
        jumps++;
      }
      const p = s.pts;
      if (st.fwd) for (let i = 0; i < p.length; i += 2) pts.push(p[i]!, p[i + 1]!);
      else for (let i = p.length - 2; i >= 0; i -= 2) pts.push(p[i]!, p[i + 1]!);
    }
    // The skeleton wobbles by a pixel and jogs at junction clumps; a line drawn on a plan does
    // not. Resample at 3 px and run a short binomial filter (twice) before simplifying: pixel
    // noise and jogs go (σ ≈ 6 px), real curvature (arcs of tens of px and more) stays within a fraction of a px.
    const clean = simplify(smoothPolyline(pts, 3, 4), 0.6);
    const n = clean.length / 2;
    const cum = new Float64Array(Math.max(1, n));
    for (let i = 1; i < n; i++) cum[i] = cum[i - 1]! + Math.hypot(clean[2 * i]! - clean[2 * i - 2]!, clean[2 * i + 1]! - clean[2 * i - 1]!);
    const len = n ? cum[n - 1]! : 0;
    const blocks = Math.max(1, Math.ceil((n - 1) / BLOCK));
    const bbox = new Float64Array(blocks * 4);
    for (let b = 0; b < blocks; b++) {
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      const i0 = b * BLOCK,
        i1 = Math.min(n - 1, i0 + BLOCK);
      for (let i = i0; i <= i1; i++) {
        const x = clean[2 * i]!,
          y = clean[2 * i + 1]!;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
      bbox.set([minX, minY, maxX, maxY], b * 4);
    }
    const meanRun = inkLen / (jumps + 1);
    const dashed = gapLen / Math.max(1, inkLen + gapLen) >= 0.2 || (jumps >= 3 && meanRun <= 45);
    const seedSeg = segs[seed]!;
    return { id: this.#rails.length, loop: seedSeg.a === seedSeg.b && order.length === 1, segs: ids, segLens: lens, pts: clean, cum, len, thick: inkLen ? thickSum / inkLen : 1, inkLen, gapLen, jumps, dashed, bbox, throughStart, throughEnd };
  }

  /**
   * Follow the line onward from one end of a segment. `through`: the line goes on past where the
   * rail stopped. `learnt`: the line's weight as read so far (shared by both directions of growth).
   */
  #grow(seg: number, fwd: boolean, visited: Set<number>, learnt: { sum: number; len: number; run: number; runSum: number; gaps: number; seeded: boolean }): Step[] & { through: boolean } {
    const { segs, nodes } = this.plan;
    const out: Step[] & { through: boolean } = Object.assign([] as Step[], { through: false });
    let cur = segs[seg]!,
      curFwd = fwd;
    let total = cur.len;
    // running line weight: length-weighted over the pieces long enough to measure cleanly
    // (a short piece is often a junction blob, thicker than the line it sits on; a long
    // piece far heavier than any line on the plan is two lines running merged)
    const trusted = (c: Seg) => c.len >= TRUST_LEN && c.thick <= BLOB_RATIO * this.heavy;
    // a blob seed is taken to be made of the plan's heavy lines until a clean piece says otherwise
    let wSum = trusted(cur) ? cur.thick * cur.len : cur.thick > BLOB_RATIO * this.heavy ? this.heavy : cur.thick,
      wLen = trusted(cur) ? cur.len : 1;
    // A line far heavier than the plan's usual heavy lines is two lines running merged — unless a
    // good length of it runs at one weight, dash after dash across its gaps: then it is drawn heavy
    // on purpose (a trench, a right of way), and that weight is its own. (Two lines running merged
    // are one solid run: they never jump a gap at that weight.)
    const blobby = (c: Seg) => c.thick > BLOB_RATIO * this.heavy;
    const seedHeavy = blobby(segs[seg]!);
    const learn = (c: Seg, gap: number) => {
      if (learnt.len || !seedHeavy) return;
      if (!blobby(c)) {
        // a clean run of ordinary weight between: the heavy pieces were blobs on an ordinary line
        if (c.len >= TRUST_LEN) learnt.run = learnt.runSum = learnt.gaps = 0;
        return;
      }
      const mean = learnt.run ? learnt.runSum / learnt.run : c.thick;
      if (Math.abs(c.thick - mean) > 0.3 * mean) return;
      if (gap > 0 && learnt.run) learnt.gaps++;
      learnt.run += c.len;
      learnt.runSum += c.thick * c.len;
      if (learnt.run >= HEAVY_RUN && learnt.gaps >= 2) {
        learnt.sum = learnt.runSum;
        learnt.len = learnt.run;
      }
    };
    const adopt = () => {
      if (learnt.len && wLen <= 1) {
        wSum = learnt.sum;
        wLen = learnt.len;
      }
    };
    if (!learnt.seeded) {
      learnt.seeded = true;
      learn(cur, 0);
    }
    adopt();
    // A line of ordinary weight that runs into such a heavy line (a run of heavy pieces at one weight
    // across a dash gap, or a long solid one) ends there: it does not pass through it as through a
    // merged blob, riding the heavy line and wandering off it again.
    const into = { run: 0, sum: 0, from: -1, gaps: 0 };
    const resetInto = () => {
      into.run = into.sum = into.gaps = 0;
      into.from = -1;
    };
    const recent: number[] = [cur.len]; // lengths of the last pieces: a run of short ones is dashed line work
    const dashy = () => recent.length >= 2 && recent.every((l) => l <= 45);
    const weightOk = (c: Seg) => {
      const mean = wSum / wLen;
      // two lines running merged: pass through — though a line drawn heavy on purpose goes on at its
      // own weight, never onto a far thinner piece
      if (c.thick > BLOB_RATIO * this.heavy && !(mean > BLOB_RATIO * this.heavy && c.thick < THIN_RATIO * mean)) return true;
      return c.thick < mean ? c.thick >= THIN_RATIO * mean : mean >= THICK_RATIO * c.thick;
    };
    // the rail's last stretch of points, in travel order, for its direction over LOOK px
    let tail: number[] = curFwd ? cur.pts.slice() : reversedPts(cur.pts);
    // the tail's chord direction over [from, to] px back from its end
    const chordBack = (from: number, to: number): Vec | null => {
      const n = tail.length / 2;
      let acc = 0,
        k = n - 1,
        a: Vec | null = null,
        b: Vec | null = null;
      if (from === 0) a = { x: tail[2 * n - 2]!, y: tail[2 * n - 1]! };
      while (k > 0) {
        const seg = Math.hypot(tail[2 * k]! - tail[2 * k - 2]!, tail[2 * k + 1]! - tail[2 * k - 1]!);
        if (!a && acc + seg >= from) {
          const t = seg ? (from - acc) / seg : 0;
          a = { x: tail[2 * k]! + (tail[2 * k - 2]! - tail[2 * k]!) * t, y: tail[2 * k + 1]! + (tail[2 * k - 1]! - tail[2 * k + 1]!) * t };
        }
        if (acc + seg >= to) {
          const t = seg ? (to - acc) / seg : 0;
          b = { x: tail[2 * k]! + (tail[2 * k - 2]! - tail[2 * k]!) * t, y: tail[2 * k + 1]! + (tail[2 * k - 1]! - tail[2 * k + 1]!) * t };
          break;
        }
        acc += seg;
        k--;
      }
      if (!a || !b) return null;
      const dx = a.x - b.x,
        dy = a.y - b.y;
      const l = Math.hypot(dx, dy);
      return l < 1e-6 ? null : { x: dx / l, y: dy / l };
    };
    // Where the line is heading: its direction over the last LOOK px, turned on by the rate it
    // has been turning (an arc keeps curving; a straight line keeps straight). Curvature
    // continuity is what tells a bank's curved edge from a straight line crossing it at 10°.
    const lookBack = (): Vec => {
      const d1 = chordBack(0, LOOK);
      if (!d1) return travelDir(cur, curFwd);
      const d0 = chordBack(LOOK, 2 * LOOK);
      if (!d0) return d1;
      const turn = Math.atan2(d1.y, d1.x) - Math.atan2(d0.y, d0.x);
      const t = Math.atan2(Math.sin(turn), Math.cos(turn)) * 0.5; // half the last turn: predict the next half-look
      const cs = Math.cos(t),
        sn = Math.sin(t);
      return { x: d1.x * cs - d1.y * sn, y: d1.x * sn + d1.y * cs };
    };
    // direction of a candidate piece over LOOK px ahead, carrying on past its far node if it is short
    const lookAhead = (c: Seg, cFwd: boolean): Vec => {
      const start = cFwd ? { x: c.pts[0]!, y: c.pts[1]! } : { x: c.pts[c.pts.length - 2]!, y: c.pts[c.pts.length - 1]! };
      let piece = c,
        fwd = cFwd,
        acc = 0,
        end = start;
      const seen = new Set<number>([c.id]);
      for (let hop = 0; hop < 3; hop++) {
        const p = fwd ? piece.pts : reversedPts(piece.pts);
        for (let i = 2; i < p.length; i += 2) {
          const seg = Math.hypot(p[i]! - p[i - 2]!, p[i + 1]! - p[i - 1]!);
          if (acc + seg >= LOOK) {
            const t = seg ? (LOOK - acc) / seg : 0;
            end = { x: p[i - 2]! + (p[i]! - p[i - 2]!) * t, y: p[i - 1]! + (p[i + 1]! - p[i - 1]!) * t };
            acc = LOOK;
            break;
          }
          acc += seg;
          end = { x: p[i]!, y: p[i + 1]! };
        }
        if (acc >= LOOK) break;
        // carry on along the straightest piece at the far node
        const far = fwd ? piece.b : piece.a;
        const node = nodes[far];
        if (!node) break;
        const dir = travelDir(piece, fwd);
        let next: { seg: Seg; fwd: boolean } | null = null,
          best = TURN_COS;
        for (const id of node.segs) {
          if (seen.has(id) || id === cur.id) continue;
          const d = segs[id]!;
          if (d.a === d.b || d.leader) continue;
          const dFwd = d.a === far;
          if (!dFwd && d.b !== far) continue;
          const w = dirInto(d, dFwd);
          const sc = dir.x * w.x + dir.y * w.y;
          if (sc > best) {
            best = sc;
            next = { seg: d, fwd: dFwd };
          }
        }
        if (!next) break;
        seen.add(next.seg.id);
        piece = next.seg;
        fwd = next.fwd;
      }
      const dx = end.x - start.x,
        dy = end.y - start.y;
      const l = Math.hypot(dx, dy);
      return l < 1e-6 ? dirInto(c, cFwd) : { x: dx / l, y: dy / l };
    };
    let jumped = 0; // gaps jumped so far: a line that has jumped a couple is dashed
    for (let guard = 0; guard < MAX_SEGS && total < MAX_LEN; guard++) {
      const endNode = curFwd ? cur.b : cur.a;
      const u = lookBack(); // direction of travel arriving at the end, over the last LOOK px
      const pEnd = endPoint(cur, curFwd);
      // 1. straightest continuation at the node
      let best: Step | null = null,
        bestScore = TURN_COS;
      const node = nodes[endNode];
      // every piece leaving this node with its direction, to recognise a line passing through
      const leaving: { id: number; w: Vec; thick: number }[] = [];
      if (node) for (const id of node.segs) {
        const c = segs[id]!;
        if (id === cur.id || c.a === c.b || c.leader) continue;
        const cFwd = c.a === endNode;
        if (!cFwd && c.b !== endNode) continue;
        leaving.push({ id, w: dirInto(c, cFwd), thick: c.thick });
      }
      if (node) for (const id of node.segs) {
        if (visited.has(id)) continue;
        const c = segs[id]!;
        if (c.a === c.b || c.leader) continue; // a loop (a circle) is never a continuation, nor a leader
        const cFwd = c.a === endNode;
        if (!cFwd && c.b !== endNode) continue;
        // a crossing line of a different weight is not this line
        if (!weightOk(c)) continue;
        const w = lookAhead(c, cFwd);
        const w0 = dirInto(c, cFwd);
        // a piece with an opposite partner at this node is a line crossing here, not our continuation —
        // unless this is a line drawn heavy on purpose going on at its weight, and the partner a far
        // thinner line (the crossing line itself, passing through)
        const heavyOn = wSum / wLen > BLOB_RATIO * this.heavy && c.thick >= THIN_RATIO * (wSum / wLen);
        const through = leaving.some((o) => o.id !== id && o.w.x * w0.x + o.w.y * w0.y < -THROUGH_PARTNER_COS && !(heavyOn && o.thick < THIN_RATIO * c.thick));
        // straightest wins; a thinner piece is suspect (another line), a thicker one is normal at a
        // junction; between near-ties the longer piece is the line itself rather than a short stub
        const mean = wSum / wLen;
        const rel = (c.thick - mean) / Math.max(1, mean);
        const score = u.x * w.x + u.y * w.y - (rel < 0 ? 0.4 * Math.min(1, -rel) : 0.05 * Math.min(1, rel)) - (through ? 0.15 : 0);
        if (score > bestScore) {
          bestScore = score;
          best = { seg: id, fwd: cFwd, gap: 0 };
        }
      }
      // 2. a gap: a symbol on the line, a dash, text that was removed
      const gapStep = (): Step | null => {
        let found: Step | null = null;
        let bestGap = JUMP + 1;
        for (const id of this.#near(pEnd, JUMP)) {
          if (visited.has(id)) continue;
          const c = segs[id]!;
          if (c.a === c.b) continue;
          if (!weightOk(c)) continue;
          if (dashy() && c.len > 90) continue; // a dashed line does not turn into a solid one across a gap
          for (const cFwd of [true, false]) {
            const pe = cFwd ? { x: c.pts[0]!, y: c.pts[1]! } : { x: c.pts[c.pts.length - 2]!, y: c.pts[c.pts.length - 1]! };
            const vx = pe.x - pEnd.x,
              vy = pe.y - pEnd.y;
            const g = Math.hypot(vx, vy);
            if (g < 0.5 || g >= bestGap) continue;
            const along = vx * u.x + vy * u.y;
            if (along <= 0) continue;
            const lateral = Math.abs(vx * u.y - vy * u.x);
            // (a heavy line's pieces either side of a line crossing it end a few px off each other's
            // line — the skeleton bends toward the crossing — so the allowance grows with its weight)
            if (lateral > Math.max(2.5, 0.12 * g, learnt.len ? 0.6 * (wSum / wLen) : 0)) continue;
            const w = dirInto(c, cFwd);
            if (u.x * w.x + u.y * w.y < JUMP_COS) continue;
            bestGap = g;
            found = { seg: id, fwd: cFwd, gap: g };
          }
        }
        return found;
      };
      if (!best) best = gapStep();
      else if (learnt.len && jumped >= 2 && best.gap === 0) {
        // A dashed line meeting a line that crosses it at a shallow angle, right where a dash ends:
        // the piece leaving the junction turns off along the crossing line, while the next dash lies
        // straight on across the gap. The dash is the line.
        const c = segs[best.seg]!;
        const w = lookAhead(c, best.fwd);
        if (u.x * w.x + u.y * w.y < DASH_TURN_COS) {
          const g = gapStep();
          if (g) {
            const gw = dirInto(segs[g.seg]!, g.fwd);
            if (u.x * gw.x + u.y * gw.y >= DASH_STRAIGHT_COS) best = g;
          }
        }
      }
      if (!best) {
        // the rail stops here; does the line itself carry on (another weight, a sharper bend)?
        if (node) for (const id of node.segs) {
          if (visited.has(id)) continue;
          const c = segs[id]!;
          if (c.a === c.b || c.leader) continue;
          const cFwd = c.a === endNode;
          if (!cFwd && c.b !== endNode) continue;
          const w = dirInto(c, cFwd);
          if (u.x * w.x + u.y * w.y >= THROUGH_COS) out.through = true;
        }
        break;
      }
      visited.add(best.seg);
      out.push(best);
      if (best.gap > 0) jumped++;
      cur = segs[best.seg]!;
      curFwd = best.fwd;
      total += cur.len + best.gap;
      tail = tail.concat(curFwd ? cur.pts : reversedPts(cur.pts));
      if (tail.length > 400) tail = tail.slice(tail.length - 400);
      if (trusted(cur)) {
        wSum += cur.thick * cur.len;
        wLen += cur.len;
      }
      learn(cur, best.gap);
      adopt();
      if (learnt.len && blobby(cur) && wLen > 1 && wSum / wLen > BLOB_RATIO * this.heavy) {
        wSum += cur.thick * cur.len;
        wLen += cur.len;
      }
      if (!seedHeavy) {
        if (blobby(cur)) {
          const mean = into.run ? into.sum / into.run : cur.thick;
          if (Math.abs(cur.thick - mean) > 0.3 * mean) resetInto();
          if (into.from < 0) into.from = out.length - 1;
          else if (best.gap > 0) into.gaps++;
          into.run += cur.len;
          into.sum += cur.thick * cur.len;
          if (into.run >= HEAVY_RUN && into.gaps >= 2) {
            out.length = into.from;
            out.through = true;
            break;
          }
        } else if (cur.len >= TRUST_LEN) resetInto();
      }
      recent.push(cur.len);
      if (recent.length > 3) recent.shift();
    }
    return out;
  }
}

/* ---------------------------------------------------------------- geometry */

/** Resample a polyline at `step` px, then apply a 5-tap binomial filter `passes` times (ends pinned). */
export function smoothPolyline(pts: number[], step: number, passes: number): number[] {
  const n = pts.length / 2;
  if (n < 3) return pts.slice();
  // resample
  const cum = [0];
  for (let i = 1; i < n; i++) cum.push(cum[i - 1]! + Math.hypot(pts[2 * i]! - pts[2 * i - 2]!, pts[2 * i + 1]! - pts[2 * i - 1]!));
  const total = cum[n - 1]!;
  if (total < 2 * step) return pts.slice();
  const m = Math.max(3, Math.round(total / step));
  const rs: number[] = [];
  let k = 0;
  for (let i = 0; i <= m; i++) {
    const s = (i / m) * total;
    while (k < n - 2 && cum[k + 1]! < s) k++;
    const l = cum[k + 1]! - cum[k]! || 1;
    const t = Math.max(0, Math.min(1, (s - cum[k]!) / l));
    rs.push(pts[2 * k]! + (pts[2 * k + 2]! - pts[2 * k]!) * t, pts[2 * k + 1]! + (pts[2 * k + 3]! - pts[2 * k + 1]!) * t);
  }
  let cur = rs;
  const count = cur.length / 2;
  for (let pass = 0; pass < passes; pass++) {
    const out = cur.slice();
    for (let i = 2; i < count - 2; i++) {
      for (const c of [0, 1]) {
        out[2 * i + c] = (cur[2 * (i - 2) + c]! + 4 * cur[2 * (i - 1) + c]! + 6 * cur[2 * i + c]! + 4 * cur[2 * (i + 1) + c]! + cur[2 * (i + 2) + c]!) / 16;
      }
    }
    cur = out;
  }
  return cur;
}

function endPoint(s: Seg, fwd: boolean): Vec {
  return fwd ? { x: s.pts[s.pts.length - 2]!, y: s.pts[s.pts.length - 1]! } : { x: s.pts[0]!, y: s.pts[1]! };
}

/** Unit direction from one end of a segment into it (fromStart: leaving a; else leaving b). */
function dirInto(s: Seg, fromStart: boolean): Vec {
  const p = s.pts;
  const n = p.length / 2;
  let ax: number, ay: number, bx: number, by: number;
  if (fromStart) {
    ax = p[0]!;
    ay = p[1]!;
    let acc = 0,
      i = 1;
    while (i < n - 1 && acc + Math.hypot(p[2 * i]! - p[2 * i - 2]!, p[2 * i + 1]! - p[2 * i - 1]!) < PROBE) {
      acc += Math.hypot(p[2 * i]! - p[2 * i - 2]!, p[2 * i + 1]! - p[2 * i - 1]!);
      i++;
    }
    const seg = Math.hypot(p[2 * i]! - p[2 * i - 2]!, p[2 * i + 1]! - p[2 * i - 1]!) || 1;
    const t = Math.min(1, (PROBE - acc) / seg);
    bx = p[2 * i - 2]! + (p[2 * i]! - p[2 * i - 2]!) * t;
    by = p[2 * i - 1]! + (p[2 * i + 1]! - p[2 * i - 1]!) * t;
  } else {
    ax = p[2 * n - 2]!;
    ay = p[2 * n - 1]!;
    let acc = 0,
      i = n - 2;
    while (i > 0 && acc + Math.hypot(p[2 * i + 2]! - p[2 * i]!, p[2 * i + 3]! - p[2 * i + 1]!) < PROBE) {
      acc += Math.hypot(p[2 * i + 2]! - p[2 * i]!, p[2 * i + 3]! - p[2 * i + 1]!);
      i--;
    }
    const seg = Math.hypot(p[2 * i + 2]! - p[2 * i]!, p[2 * i + 3]! - p[2 * i + 1]!) || 1;
    const t = Math.min(1, (PROBE - acc) / seg);
    bx = p[2 * i + 2]! + (p[2 * i]! - p[2 * i + 2]!) * t;
    by = p[2 * i + 3]! + (p[2 * i + 1]! - p[2 * i + 3]!) * t;
  }
  const l = Math.hypot(bx - ax, by - ay) || 1;
  return { x: (bx - ax) / l, y: (by - ay) / l };
}

function reversedPts(p: number[]): number[] {
  const out: number[] = [];
  for (let i = p.length - 2; i >= 0; i -= 2) out.push(p[i]!, p[i + 1]!);
  return out;
}

/** Unit direction of travel when arriving at the far end of a segment. */
function travelDir(s: Seg, fwd: boolean): Vec {
  const d = dirInto(s, !fwd); // from the far end back into the segment
  return { x: -d.x, y: -d.y };
}

/** Nearest point of a rail to p, or null when farther than maxD. */
export function projectOnRail(r: Rail, p: Vec, maxD: number): Projection | null {
  const { pts, cum, bbox } = r;
  const n = pts.length / 2;
  if (n < 2) return null;
  let best = maxD,
    bi = -1,
    bt = 0,
    bx = 0,
    by = 0;
  const blocks = bbox.length / 4;
  for (let b = 0; b < blocks; b++) {
    if (p.x < bbox[b * 4]! - best || p.x > bbox[b * 4 + 2]! + best || p.y < bbox[b * 4 + 1]! - best || p.y > bbox[b * 4 + 3]! + best) continue;
    const i0 = b * BLOCK,
      i1 = Math.min(n - 2, i0 + BLOCK - 1);
    for (let i = i0; i <= i1; i++) {
      const q = nearestOnSegment(p, { x: pts[2 * i]!, y: pts[2 * i + 1]! }, { x: pts[2 * i + 2]!, y: pts[2 * i + 3]! });
      if (q.d < best) {
        best = q.d;
        bi = i;
        bt = q.t;
        bx = q.p.x;
        by = q.p.y;
      }
    }
  }
  if (bi < 0) return null;
  const sx = pts[2 * bi + 2]! - pts[2 * bi]!,
    sy = pts[2 * bi + 3]! - pts[2 * bi + 1]!;
  const sl = Math.hypot(sx, sy) || 1;
  const tx = sx / sl,
    ty = sy / sl;
  let over = 0;
  if (bi === 0 && bt === 0) over = -((p.x - pts[0]!) * tx + (p.y - pts[1]!) * ty);
  else if (bi === n - 2 && bt === 1) over = (p.x - pts[2 * n - 2]!) * tx + (p.y - pts[2 * n - 1]!) * ty;
  return { s: cum[bi]! + bt * sl, d: best, x: bx, y: by, tx, ty, over: Math.max(0, over) };
}

export interface Box {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Do two rails cross inside the box — an X, with both lines continuing at least
 * `tail` px past the meeting point — or the line physically continuing where the
 * rail stopped? A leader ending on a line (a T) or two edges merging (a V) do not count.
 */
export function railsCross(a: Rail, b: Rail, box: Box, tail = 10): boolean {
  if (a === b) return false;
  const na = a.pts.length / 2,
    nb = b.pts.length / 2;
  const ba = a.bbox.length / 4,
    bb = b.bbox.length / 4;
  const inBox = (bx: Float64Array, i: number) => !(bx[i * 4 + 2]! < box.minX || bx[i * 4]! > box.maxX || bx[i * 4 + 3]! < box.minY || bx[i * 4 + 1]! > box.maxY);
  for (let i = 0; i < ba; i++) {
    if (!inBox(a.bbox, i)) continue;
    for (let j = 0; j < bb; j++) {
      if (!inBox(b.bbox, j)) continue;
      if (a.bbox[i * 4 + 2]! < b.bbox[j * 4]! || a.bbox[i * 4]! > b.bbox[j * 4 + 2]! || a.bbox[i * 4 + 3]! < b.bbox[j * 4 + 1]! || a.bbox[i * 4 + 1]! > b.bbox[j * 4 + 3]!) continue;
      const p1 = Math.min(na - 2, i * BLOCK + BLOCK - 1),
        q1 = Math.min(nb - 2, j * BLOCK + BLOCK - 1);
      for (let p = i * BLOCK; p <= p1; p++) {
        const ax = a.pts[2 * p]!,
          ay = a.pts[2 * p + 1]!,
          adx = a.pts[2 * p + 2]! - ax,
          ady = a.pts[2 * p + 3]! - ay;
        for (let q = j * BLOCK; q <= q1; q++) {
          const bx = b.pts[2 * q]!,
            by = b.pts[2 * q + 1]!,
            bdx = b.pts[2 * q + 2]! - bx,
            bdy = b.pts[2 * q + 3]! - by;
          const den = adx * bdy - ady * bdx;
          if (Math.abs(den) < 1e-9) continue; // parallel or collinear: no crossing
          const t = ((bx - ax) * bdy - (by - ay) * bdx) / den;
          const u = ((bx - ax) * ady - (by - ay) * adx) / den;
          if (t < 0 || t > 1 || u < 0 || u > 1) continue;
          const sA = a.cum[p]! + t * Math.hypot(adx, ady),
            sB = b.cum[q]! + u * Math.hypot(bdx, bdy);
          const aOn = (sA >= tail || a.throughStart) && (a.len - sA >= tail || a.throughEnd);
          const bOn = (sB >= tail || b.throughStart) && (b.len - sB >= tail || b.throughEnd);
          if (aOn && bOn) return true;
        }
      }
    }
  }
  return false;
}

/** The piece (segment) of the rail that arc length s falls in: its length and thickness — a stray stub is short, a line is long. */
export function railPieceAt(r: Rail, s: number, plan: VectorPlan): { len: number; thick: number } {
  let acc = 0;
  for (let k = 0; k < r.segLens.length; k++) {
    acc += r.segLens[k]!;
    if (acc >= s) return { len: r.segLens[k]!, thick: plan.segs[r.segs[k]!]!.thick };
  }
  const last = r.segs.length - 1;
  return last >= 0 ? { len: r.segLens[last]!, thick: plan.segs[r.segs[last]!]!.thick } : { len: 0, thick: 0 };
}

/** Point of a rail at arc length s (clamped). */
export function railPointAt(r: Rail, s: number): Vec {
  const { pts, cum } = r;
  const n = pts.length / 2;
  if (s <= 0) return { x: pts[0]!, y: pts[1]! };
  if (s >= r.len) return { x: pts[2 * n - 2]!, y: pts[2 * n - 1]! };
  const i = upperIndex(cum, s) - 1;
  const l = cum[i + 1]! - cum[i]! || 1;
  const t = (s - cum[i]!) / l;
  return { x: pts[2 * i]! + (pts[2 * i + 2]! - pts[2 * i]!) * t, y: pts[2 * i + 1]! + (pts[2 * i + 3]! - pts[2 * i + 1]!) * t };
}

/** The rail's polyline between arc lengths s0 < s1 (both ends interpolated). */
export function railSlice(r: Rail, s0: number, s1: number): number[] {
  const { pts, cum } = r;
  const n = pts.length / 2;
  const a = Math.max(0, Math.min(r.len, s0)),
    b = Math.max(0, Math.min(r.len, s1));
  if (b - a < 0.5 || n < 2) return [];
  const out: number[] = [];
  const pa = railPointAt(r, a);
  out.push(pa.x, pa.y);
  for (let i = upperIndex(cum, a); i < n && cum[i]! < b; i++) {
    if (cum[i]! - a < 0.25) continue;
    out.push(pts[2 * i]!, pts[2 * i + 1]!);
  }
  const pb = railPointAt(r, b);
  if (Math.hypot(pb.x - out[out.length - 2]!, pb.y - out[out.length - 1]!) >= 0.25) out.push(pb.x, pb.y);
  return out;
}

/** First index whose cumulative length is greater than s. */
function upperIndex(cum: Float64Array, s: number): number {
  let lo = 0,
    hi = cum.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cum[mid]! <= s) lo = mid + 1;
    else hi = mid;
  }
  return Math.min(cum.length - 1, Math.max(1, lo));
}
